import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { nutritionApi } from '../api';

const ML_PER_CUP = 250;

const STORAGE_PREFIX = 'xiaobenben_fluid_logs_v2_';

type TypeId = 'pure' | 'coffee' | 'soda' | 'juice' | 'alcohol';

interface TypeConfig {
  name: string;
  icon: string;
  presets: { label: string; ml: number }[];
  defaultSugar: boolean;
  defaultAlcohol: boolean;
  /** 无食物库时用估算热量 kcal/100ml（液体按 g≈ml） */
  kcalPer100ml: number;
}

const TYPE_CONFIGS: Record<TypeId, TypeConfig> = {
  pure: {
    name: '纯净水',
    icon: '💧',
    presets: [
      { label: '一小杯', ml: 200 },
      { label: '普通杯', ml: 350 },
      { label: '大杯', ml: 500 },
      { label: '瓶装水', ml: 550 },
    ],
    defaultSugar: false,
    defaultAlcohol: false,
    kcalPer100ml: 0,
  },
  coffee: {
    name: '咖啡/茶',
    icon: '☕',
    presets: [
      { label: '中杯 (M)', ml: 350 },
      { label: '大杯 (L)', ml: 470 },
      { label: '超大杯', ml: 600 },
      { label: '功夫茶杯', ml: 50 },
    ],
    defaultSugar: false,
    defaultAlcohol: false,
    kcalPer100ml: 40,
  },
  soda: {
    name: '饮料/汽水',
    icon: '🥤',
    presets: [
      { label: '标准听装', ml: 330 },
      { label: '细长听装', ml: 200 },
      { label: '常规瓶装', ml: 500 },
      { label: '超大瓶', ml: 888 },
    ],
    defaultSugar: true,
    defaultAlcohol: false,
    kcalPer100ml: 43,
  },
  juice: {
    name: '果汁/奶饮',
    icon: '🍹',
    presets: [
      { label: '利乐包', ml: 250 },
      { label: '标准杯', ml: 350 },
      { label: '大杯奶茶', ml: 650 },
      { label: '家庭装', ml: 1000 },
    ],
    defaultSugar: true,
    defaultAlcohol: false,
    kcalPer100ml: 55,
  },
  alcohol: {
    name: '酒类',
    icon: '🍺',
    presets: [
      { label: '小酒盅', ml: 20 },
      { label: '标准罐', ml: 330 },
      { label: '精酿杯', ml: 500 },
      { label: '标准瓶', ml: 600 },
    ],
    defaultSugar: false,
    defaultAlcohol: true,
    kcalPer100ml: 45,
  },
};

const TYPE_IDS: TypeId[] = ['pure', 'coffee', 'soda', 'juice', 'alcohol'];

interface FluidLogEntry {
  id: string;
  time: string;
  amount: number;
  typeId: TypeId;
  typeName: string;
  icon: string;
  spec: string;
  sugar: boolean;
  alcohol: boolean;
  mealRecordId?: number;
  fromServerSeed?: boolean;
}

interface WaterDrinkModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

function loadLogs(date: string): FluidLogEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + date);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveLogs(date: string, logs: FluidLogEntry[]) {
  try {
    localStorage.setItem(STORAGE_PREFIX + date, JSON.stringify(logs));
  } catch {
    /* ignore */
  }
}

const WaterDrinkModal: React.FC<WaterDrinkModalProps> = ({ open, onClose, onSuccess }) => {
  const today = useMemo(() => {
    const d = new Date();
    const p = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  }, []);

  const [loading, setLoading] = useState(true);
  const [goalMl, setGoalMl] = useState(2000);
  const [logs, setLogs] = useState<FluidLogEntry[]>([]);
  const [currentTypeId, setCurrentTypeId] = useState<TypeId>('pure');
  const [hasSugar, setHasSugar] = useState(false);
  const [hasAlcohol, setHasAlcohol] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [showCustomModal, setShowCustomModal] = useState(false);
  const [customMl, setCustomMl] = useState('');

  const config = TYPE_CONFIGS[currentTypeId];

  const totalMl = useMemo(() => logs.reduce((s, l) => s + l.amount, 0), [logs]);
  const progressPct = Math.min(goalMl > 0 ? (totalMl / goalMl) * 100 : 0, 100);

  const refreshFromServer = useCallback(async () => {
    setLoading(true);
    try {
      const res = await nutritionApi.getDailySummary(today);
      const s = (res.data as any)?.summary;
      const g = (res.data as any)?.goal;
      const tc = Math.max(1, Number(g?.target_water_cups ?? 8));
      setGoalMl(tc * ML_PER_CUP);

      const waterCups = Math.max(0, Number(s?.water_cups ?? 0));
      let next = loadLogs(today);
      if (next.length === 0 && waterCups > 0) {
        next = [
          {
            id: `seed-${today}`,
            time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }),
            amount: waterCups * ML_PER_CUP,
            typeId: 'pure',
            typeName: TYPE_CONFIGS.pure.name,
            icon: TYPE_CONFIGS.pure.icon,
            spec: '历史同步',
            sugar: false,
            alcohol: false,
            fromServerSeed: true,
          },
        ];
        saveLogs(today, next);
      }
      setLogs(next);
    } catch {
      setMsg('加载失败');
      setLogs(loadLogs(today));
    } finally {
      setLoading(false);
    }
  }, [today]);

  useEffect(() => {
    if (!open) return;
    setMsg(null);
    setShowCustomModal(false);
    setCustomMl('');
    setCurrentTypeId('pure');
    setHasSugar(TYPE_CONFIGS.pure.defaultSugar);
    setHasAlcohol(TYPE_CONFIGS.pure.defaultAlcohol);
    void refreshFromServer();
  }, [open, refreshFromServer]);

  useEffect(() => {
    const c = TYPE_CONFIGS[currentTypeId];
    setHasSugar(c.defaultSugar);
    setHasAlcohol(c.defaultAlcohol);
  }, [currentTypeId]);

  const persistLogs = (next: FluidLogEntry[]) => {
    setLogs(next);
    saveLogs(today, next);
  };

  const syncPureWater = useCallback(
    async (pureLogs: FluidLogEntry[]) => {
      const pureMl = pureLogs.filter((l) => l.typeId === 'pure').reduce((s, l) => s + l.amount, 0);
      const cups = Math.round(pureMl / ML_PER_CUP);
      await nutritionApi.addWater({ cups: Math.max(0, cups), date: today });
    },
    [today]
  );

  const quickAdd = async (ml: number, specLabel: string) => {
    const c = TYPE_CONFIGS[currentTypeId];
    const amount = Math.max(1, Math.floor(ml));
    const entry: FluidLogEntry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }),
      amount,
      typeId: currentTypeId,
      typeName: c.name,
      icon: c.icon,
      spec: specLabel,
      sugar: hasSugar,
      alcohol: hasAlcohol,
    };

    setBusy(true);
    setMsg(null);
    try {
      if (currentTypeId === 'pure') {
        const next = [entry, ...logs];
        persistLogs(next);
        await syncPureWater(next);
      } else {
        const kcal = (c.kcalPer100ml * amount) / 100;
        const note = `xb_fluid:${JSON.stringify({ typeId: currentTypeId, sugar: hasSugar, alcohol: hasAlcohol, spec: specLabel })}`;
        const res = await nutritionApi.createMeal({
          food_name: `${c.name} · ${specLabel}`,
          meal_type: 'snack',
          amount,
          calories: Math.round(kcal * 10) / 10,
          date: today,
          note,
        });
        const mealId = (res.data as any)?.meal?.id;
        if (mealId) entry.mealRecordId = mealId;
        persistLogs([entry, ...logs]);
      }
      onSuccess?.();
    } catch {
      setMsg('记录失败，请重试');
    } finally {
      setBusy(false);
    }
  };

  const confirmCustom = async () => {
    const v = parseInt(customMl, 10);
    if (!v || v <= 0) return;
    setShowCustomModal(false);
    setCustomMl('');
    await quickAdd(v, '自定义');
  };

  const deleteLog = async (id: string) => {
    const target = logs.find((l) => l.id === id);
    if (!target) return;
    setBusy(true);
    setMsg(null);
    try {
      const next = logs.filter((l) => l.id !== id);
      persistLogs(next);
      if (target.typeId === 'pure') {
        await syncPureWater(next);
      } else if (target.mealRecordId) {
        await nutritionApi.deleteMeal(target.mealRecordId);
      }
      onSuccess?.();
    } catch {
      setMsg('删除失败');
    } finally {
      setBusy(false);
    }
  };

  const clearAll = async () => {
    if (!window.confirm('清空今日全部饮水与饮品记录？')) return;
    setBusy(true);
    setMsg(null);
    try {
      const mealIds = logs.filter((l) => l.mealRecordId).map((l) => l.mealRecordId!);
      for (const mid of mealIds) {
        try {
          await nutritionApi.deleteMeal(mid);
        } catch {
          /* ignore single */
        }
      }
      await nutritionApi.addWater({ cups: 0, date: today });
      persistLogs([]);
      onSuccess?.();
    } catch {
      setMsg('清空失败');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="wd-overlay" onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="wd-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="wd-topbar">
          <span className="wd-topbar-spacer" />
          <h2 className="wd-title">饮水与饮品</h2>
          <button type="button" className="wd-close" onClick={() => !busy && onClose()} aria-label="关闭">
            ✕
          </button>
        </div>

        {msg && (
          <div className={`wd-toast ${msg.includes('失败') ? 'err' : 'ok'}`}>{msg}</div>
        )}

        <div className="wd-light">
          {/* 头部进度 */}
          <div className="wd-glass-card">
            <div className="wd-glass-inner">
              <p className="wd-progress-label">今日饮水进度</p>
              <div className="wd-progress-big">
                <span className="wd-num">{loading ? '…' : totalMl}</span>
                <span className="wd-num-suffix"> / {goalMl} ml</span>
              </div>
              <div className="wd-bar-track">
                <div className="wd-bar-fill" style={{ width: `${progressPct}%` }} />
              </div>
            </div>
            <div className="wd-water-bg" style={{ height: `${progressPct}%` }} />
          </div>

          {/* 常用容量 */}
          <div className="wd-card">
            <div className="wd-card-head">
              <label className="wd-label-upper">
                常用容量 (<span>{config.name}</span>)
              </label>
              <div className="wd-toggles">
                <label className="wd-check">
                  <input type="checkbox" checked={hasSugar} onChange={(e) => setHasSugar(e.target.checked)} disabled={busy} />
                  <span>含糖</span>
                </label>
                <label className="wd-check">
                  <input type="checkbox" checked={hasAlcohol} onChange={(e) => setHasAlcohol(e.target.checked)} disabled={busy} />
                  <span>酒精</span>
                </label>
              </div>
            </div>
            <div className="wd-preset-grid">
              {config.presets.map((p) => (
                <button
                  key={p.label + p.ml}
                  type="button"
                  className="wd-preset-btn"
                  disabled={busy || loading}
                  onClick={() => void quickAdd(p.ml, p.label)}
                >
                  <span className="wd-preset-label">{p.label}</span>
                  <span className="wd-preset-ml">{p.ml}ml</span>
                </button>
              ))}
            </div>
            <button type="button" className="wd-custom-cap" disabled={busy || loading} onClick={() => setShowCustomModal(true)}>
              + 自定义容量
            </button>
          </div>

          {/* 种类 */}
          <div className="wd-type-block">
            <label className="wd-type-title">选择种类</label>
            <div className="wd-type-scroll">
              {TYPE_IDS.map((id) => {
                const t = TYPE_CONFIGS[id];
                const active = currentTypeId === id;
                return (
                  <button
                    key={id}
                    type="button"
                    className={`wd-type-btn ${active ? 'active' : ''}`}
                    onClick={() => setCurrentTypeId(id)}
                  >
                    <span className="wd-type-emoji">{t.icon}</span>
                    <span className="wd-type-text">
                      {id === 'pure' && '水'}
                      {id === 'coffee' && '咖/茶'}
                      {id === 'soda' && '饮料'}
                      {id === 'juice' && '果汁奶'}
                      {id === 'alcohol' && '酒类'}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 今日记录 */}
          <div className="wd-history-card">
            <div className="wd-history-head">
              <h3 className="wd-history-title">今日记录</h3>
              <button type="button" className="wd-clear" disabled={busy || logs.length === 0} onClick={() => void clearAll()}>
                清空
              </button>
            </div>
            <div className="wd-history-list">
              {logs.length === 0 ? (
                <p className="wd-empty">还没有记录，开始喝水吧！</p>
              ) : (
                logs.map((log) => (
                  <div key={log.id} className="wd-log-row">
                    <div className="wd-log-left">
                      <div className="wd-log-icon">{log.icon}</div>
                      <div>
                        <div className="wd-log-line1">
                          <span className="wd-log-ml">{log.amount}ml</span>
                          <span className="wd-log-type">
                            {log.typeName}
                            {log.spec ? ` (${log.spec})` : ''}
                          </span>
                        </div>
                        <div className="wd-log-meta">
                          <span className="wd-log-time">{log.time}</span>
                          {log.sugar && <span className="wd-tag sugar">糖</span>}
                          {log.alcohol && <span className="wd-tag alcohol">酒</span>}
                        </div>
                      </div>
                    </div>
                    <button type="button" className="wd-del" disabled={busy} onClick={() => void deleteLog(log.id)} aria-label="删除">
                      <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          <p className="wd-footnote">
            纯水写入「杯数」统计（约 {ML_PER_CUP}ml/杯）；其他种类记入加餐并估算热量。多设备同步以服务端为准，本地列表可能与历史不完全一致。
          </p>
        </div>
      </div>

      {/* 自定义容量 */}
      {showCustomModal && (
        <div className="wd-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowCustomModal(false)}>
          <div className="wd-modal-box" onClick={(e) => e.stopPropagation()}>
            <h3 className="wd-modal-title">手动输入</h3>
            <p className="wd-modal-desc">请输入本次摄入的容量 (ml)</p>
            <input
              type="number"
              className="wd-modal-input"
              placeholder="450"
              value={customMl}
              onChange={(e) => setCustomMl(e.target.value)}
              min={1}
              autoFocus
            />
            <div className="wd-modal-actions">
              <button type="button" className="wd-modal-cancel" onClick={() => setShowCustomModal(false)}>
                取消
              </button>
              <button type="button" className="wd-modal-ok" onClick={() => void confirmCustom()}>
                确认
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        /* ── Overlay ── */
        .wd-overlay {
          position: fixed; inset: 0; z-index: 9999;
          background: rgba(2, 6, 23, 0.88);
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
          display: flex; align-items: center; justify-content: center;
          padding: 16px;
          animation: wdFadeIn 0.22s cubic-bezier(0.16, 1, 0.3, 1);
        }
        @keyframes wdFadeIn { from { opacity: 0; } to { opacity: 1; } }

        /* ── Sheet ── */
        .wd-sheet {
          width: 100%; max-width: 430px; max-height: 92vh;
          overflow: hidden;
          display: flex; flex-direction: column;
          border-radius: 26px;
          background: rgba(10, 15, 36, 0.97);
          border: 1px solid rgba(148, 163, 184, 0.12);
          box-shadow:
            0 0 0 1px rgba(56, 189, 248, 0.05),
            0 30px 90px rgba(0, 0, 0, 0.7),
            0 0 60px rgba(56, 189, 248, 0.04);
          animation: wdSlideUp 0.28s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        @keyframes wdSlideUp {
          from { transform: translateY(24px) scale(0.97); opacity: 0.6; }
          to   { transform: translateY(0)    scale(1);    opacity: 1; }
        }

        /* ── Topbar ── */
        .wd-topbar {
          display: grid; grid-template-columns: 40px 1fr 40px;
          align-items: center; padding: 16px 16px 10px;
          background: linear-gradient(180deg, rgba(14,22,54,1) 0%, rgba(10,15,36,0) 100%);
          flex-shrink: 0;
        }
        .wd-topbar-spacer { width: 40px; }
        .wd-title {
          margin: 0; font-size: 17px; font-weight: 800; text-align: center;
          background: linear-gradient(90deg, #7dd3fc, #e2e8f0);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          letter-spacing: 0.2px;
        }
        .wd-close {
          width: 34px; height: 34px; border-radius: 999px; justify-self: end;
          border: 1px solid rgba(148,163,184,0.2);
          background: rgba(30,41,59,0.7); color: rgba(148,163,184,0.8);
          cursor: pointer; font-size: 15px; display: flex; align-items: center; justify-content: center;
          transition: background 0.15s;
        }
        .wd-close:hover { background: rgba(30,41,59,1); color: #e2e8f0; }

        /* ── Toast ── */
        .wd-toast {
          margin: 0 16px 10px; padding: 9px 14px; border-radius: 12px;
          font-size: 13px; font-weight: 600; flex-shrink: 0;
        }
        .wd-toast.ok { background: rgba(16,185,129,0.12); color: #6ee7b7; border: 1px solid rgba(16,185,129,0.2); }
        .wd-toast.err { background: rgba(244,63,94,0.12); color: #fca5a5; border: 1px solid rgba(244,63,94,0.2); }

        /* ── Scrollable body ── */
        .wd-light {
          overflow-y: auto; overflow-x: hidden;
          padding: 0 14px 24px;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: thin;
          scrollbar-color: rgba(56,189,248,0.2) transparent;
        }
        .wd-light::-webkit-scrollbar { width: 4px; }
        .wd-light::-webkit-scrollbar-thumb { background: rgba(56,189,248,0.2); border-radius: 99px; }

        /* ── Progress glass card ── */
        .wd-glass-card {
          position: relative; margin-top: 6px; margin-bottom: 16px;
          border-radius: 22px; overflow: hidden;
          background: linear-gradient(145deg, rgba(14,22,54,0.9) 0%, rgba(7,27,71,0.85) 100%);
          border: 1px solid rgba(56,189,248,0.18);
          box-shadow: 0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.04);
        }
        .wd-glass-inner { position: relative; z-index: 2; padding: 22px 20px; text-align: center; }
        .wd-progress-label {
          margin: 0 0 6px; font-size: 11px; font-weight: 700;
          letter-spacing: 0.12em; text-transform: uppercase;
          color: rgba(148,163,184,0.7);
        }
        .wd-progress-big { margin-bottom: 14px; line-height: 1.05; }
        .wd-num {
          font-size: 42px; font-weight: 900;
          background: linear-gradient(135deg, #38bdf8, #7dd3fc);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          filter: drop-shadow(0 0 12px rgba(56,189,248,0.4));
        }
        .wd-num-suffix { font-size: 15px; font-weight: 600; color: rgba(148,163,184,0.5); margin-left: 2px; }
        .wd-bar-track {
          height: 10px; border-radius: 999px; overflow: hidden;
          background: rgba(30,41,59,0.8);
          border: 1px solid rgba(56,189,248,0.1);
        }
        .wd-bar-fill {
          height: 100%; border-radius: 999px;
          background: linear-gradient(90deg, #0369a1, #38bdf8);
          box-shadow: 0 0 10px rgba(56,189,248,0.5);
          transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .wd-water-bg {
          position: absolute; bottom: 0; left: 0; width: 100%;
          background: linear-gradient(0deg, rgba(56,189,248,0.12) 0%, transparent 100%);
          transition: height 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          pointer-events: none;
        }

        /* ── Card (presets) ── */
        .wd-card {
          background: rgba(15,23,42,0.7);
          border: 1px solid rgba(148,163,184,0.1);
          border-radius: 22px; padding: 18px;
          margin-bottom: 14px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.3);
        }
        .wd-card-head {
          display: flex; justify-content: space-between; align-items: flex-start;
          gap: 10px; margin-bottom: 14px; flex-wrap: wrap;
        }
        .wd-label-upper {
          font-size: 10px; font-weight: 700; letter-spacing: 0.1em;
          text-transform: uppercase; color: rgba(148,163,184,0.6);
        }
        .wd-label-upper span { color: rgba(203,213,225,0.85); text-transform: none; letter-spacing: 0; }
        .wd-toggles { display: flex; gap: 12px; }
        .wd-check {
          display: flex; align-items: center; gap: 5px;
          font-size: 12px; color: rgba(148,163,184,0.8); cursor: pointer;
        }
        .wd-check input { width: 15px; height: 15px; accent-color: #38bdf8; }

        .wd-preset-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
        .wd-preset-btn {
          display: flex; flex-direction: column; align-items: center; padding: 12px 8px;
          border-radius: 16px;
          border: 1px solid rgba(56,189,248,0.15);
          background: rgba(7,27,71,0.5);
          cursor: pointer; font-family: inherit;
          transition: transform 0.12s ease, border-color 0.15s, box-shadow 0.15s;
        }
        .wd-preset-btn:hover {
          border-color: rgba(56,189,248,0.4);
          box-shadow: 0 0 16px rgba(56,189,248,0.12);
        }
        .wd-preset-btn:active { transform: scale(0.96); }
        .wd-preset-btn:disabled { opacity: 0.35; cursor: not-allowed; }
        .wd-preset-label {
          font-size: 10px; font-weight: 700; letter-spacing: 0.04em;
          color: rgba(125,211,252,0.7); margin-bottom: 3px;
        }
        .wd-preset-ml { font-size: 18px; font-weight: 900; color: #e2e8f0; }

        .wd-custom-cap {
          width: 100%; margin-top: 10px; padding: 11px;
          border-radius: 14px;
          border: 1px dashed rgba(148,163,184,0.2);
          background: transparent; color: rgba(148,163,184,0.6);
          font-size: 13px; font-weight: 600; cursor: pointer; font-family: inherit;
          transition: border-color 0.15s, color 0.15s;
        }
        .wd-custom-cap:hover { border-color: rgba(56,189,248,0.35); color: #7dd3fc; }

        /* ── Type selector ── */
        .wd-type-block { margin-bottom: 14px; }
        .wd-type-title {
          display: block; font-size: 10px; font-weight: 700; letter-spacing: 0.1em;
          text-transform: uppercase; color: rgba(148,163,184,0.55);
          margin-bottom: 8px; padding-left: 2px;
        }
        .wd-type-scroll {
          display: flex; gap: 5px; flex-wrap: nowrap; justify-content: stretch; align-items: stretch;
        }
        .wd-type-btn {
          flex: 1 1 0; min-width: 0; padding: 9px 2px 10px;
          display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px;
          border-radius: 16px;
          border: 1px solid rgba(148,163,184,0.08);
          background: rgba(15,23,42,0.6);
          cursor: pointer; font-family: inherit;
          transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
        }
        .wd-type-btn:hover { border-color: rgba(56,189,248,0.25); }
        .wd-type-btn.active {
          border-color: rgba(56,189,248,0.5);
          background: rgba(7,27,71,0.85);
          box-shadow: 0 0 14px rgba(56,189,248,0.15);
        }
        .wd-type-emoji { font-size: 20px; line-height: 1; }
        .wd-type-text {
          font-size: 9px; font-weight: 700; color: rgba(203,213,225,0.75);
          text-align: center; line-height: 1.15; word-break: keep-all;
        }
        .wd-type-btn.active .wd-type-text { color: #7dd3fc; }

        /* ── History card ── */
        .wd-history-card {
          background: rgba(15,23,42,0.7);
          border: 1px solid rgba(148,163,184,0.1);
          border-radius: 22px; padding: 18px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.3);
        }
        .wd-history-head {
          display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px;
        }
        .wd-history-title { margin: 0; font-size: 15px; font-weight: 800; color: rgba(226,232,240,0.95); }
        .wd-clear {
          font-size: 12px; font-weight: 700; color: rgba(148,163,184,0.5);
          background: none; border: none; cursor: pointer; transition: color 0.15s;
        }
        .wd-clear:hover { color: #f87171; }
        .wd-empty {
          text-align: center; padding: 36px 16px;
          color: rgba(148,163,184,0.4); font-size: 14px; font-style: italic;
        }

        .wd-log-row {
          display: flex; align-items: center; justify-content: space-between; gap: 10px;
          padding: 12px; border-radius: 16px;
          background: rgba(30,41,59,0.4);
          border: 1px solid rgba(148,163,184,0.07);
          margin-bottom: 8px;
          transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;
        }
        .wd-log-row:hover {
          background: rgba(30,41,59,0.75);
          border-color: rgba(148,163,184,0.15);
          box-shadow: 0 4px 16px rgba(0,0,0,0.25);
        }
        .wd-log-left { display: flex; align-items: center; gap: 12px; min-width: 0; }
        .wd-log-icon {
          width: 42px; height: 42px; display: flex; align-items: center; justify-content: center;
          background: rgba(15,23,42,0.8); border-radius: 14px; font-size: 22px;
          border: 1px solid rgba(148,163,184,0.1);
          flex-shrink: 0;
        }
        .wd-log-line1 { display: flex; align-items: baseline; gap: 7px; flex-wrap: wrap; }
        .wd-log-ml { font-weight: 800; font-size: 14px; color: #e2e8f0; }
        .wd-log-type { font-size: 11px; color: rgba(148,163,184,0.65); font-weight: 600; }
        .wd-log-meta { display: flex; align-items: center; gap: 6px; margin-top: 3px; flex-wrap: wrap; }
        .wd-log-time { font-size: 10px; color: rgba(100,116,139,0.7); font-family: ui-monospace, monospace; }
        .wd-tag { padding: 1px 6px; border-radius: 999px; font-size: 9px; font-weight: 800; }
        .wd-tag.sugar { background: rgba(244,63,94,0.12); color: #fca5a5; }
        .wd-tag.alcohol { background: rgba(167,139,250,0.12); color: #c4b5fd; }
        .wd-del {
          padding: 8px; border: none; background: transparent;
          color: rgba(148,163,184,0.2); cursor: pointer; flex-shrink: 0;
          transition: color 0.15s;
        }
        .wd-log-row:hover .wd-del { color: rgba(248,113,113,0.7); }
        .wd-del:hover { color: #f87171 !important; }

        .wd-footnote {
          font-size: 11px; color: rgba(100,116,139,0.55);
          line-height: 1.5; margin: 14px 2px 0;
        }

        /* ── Custom ml modal ── */
        .wd-modal-overlay {
          position: fixed; inset: 0; z-index: 10000;
          background: rgba(2,6,23,0.75); backdrop-filter: blur(8px);
          display: flex; align-items: center; justify-content: center; padding: 24px;
        }
        .wd-modal-box {
          width: 100%; max-width: 300px;
          background: rgba(10,15,36,0.98);
          border: 1px solid rgba(56,189,248,0.2);
          border-radius: 26px; padding: 28px 22px;
          box-shadow: 0 30px 80px rgba(0,0,0,0.6), 0 0 40px rgba(56,189,248,0.06);
          animation: wdSlideUp 0.24s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        .wd-modal-title {
          margin: 0 0 6px; font-size: 18px; font-weight: 800; text-align: center;
          color: rgba(226,232,240,0.95);
        }
        .wd-modal-desc { margin: 0 0 18px; font-size: 13px; color: rgba(148,163,184,0.65); text-align: center; }
        .wd-modal-input {
          width: 100%; box-sizing: border-box;
          border: 1.5px solid rgba(148,163,184,0.2); border-radius: 16px; padding: 14px;
          font-size: 28px; font-weight: 900; text-align: center;
          color: #e2e8f0; background: rgba(15,23,42,0.9);
          margin-bottom: 18px; outline: none; font-family: inherit;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .wd-modal-input:focus {
          border-color: rgba(56,189,248,0.5);
          box-shadow: 0 0 16px rgba(56,189,248,0.12);
        }
        .wd-modal-actions { display: flex; gap: 10px; }
        .wd-modal-cancel {
          flex: 1; padding: 13px; border-radius: 14px;
          border: 1px solid rgba(148,163,184,0.15);
          background: rgba(30,41,59,0.6); color: rgba(148,163,184,0.7);
          font-weight: 700; cursor: pointer; font-family: inherit;
          transition: background 0.15s;
        }
        .wd-modal-cancel:hover { background: rgba(30,41,59,0.9); }
        .wd-modal-ok {
          flex: 1; padding: 13px; border-radius: 14px; border: none;
          background: linear-gradient(135deg, #0369a1, #38bdf8);
          color: #0f172a; font-weight: 800; cursor: pointer; font-family: inherit;
          box-shadow: 0 6px 20px rgba(56,189,248,0.3);
          transition: opacity 0.15s, transform 0.1s;
        }
        .wd-modal-ok:hover { opacity: 0.9; transform: scale(1.02); }
      `}</style>
    </div>
  );
};

export default WaterDrinkModal;
